/* eslint-disable replo/no-tailwind-literal-color */
import type { AgentState } from "@agent-ui/features/chat/hooks/useReploAgentConnection";

import * as React from "react";

import { CollapsibleJsonTree } from "@v2/features/agent-debugger/tabs/CollapsibleJsonTree";

function getCfDurableObjectUrl(durableObjectId: string): string | null {
  const accountId = process.env.REPLO_CF_ACCOUNT_ID;
  if (!accountId) {
    return null;
  }
  return `https://dash.cloudflare.com/${accountId}/workers/durable-objects/view/${durableObjectId}/overview`;
}

function getCfSandboxUrl(sandboxId: string): string | null {
  const accountId = process.env.REPLO_CF_ACCOUNT_ID;
  const appId = process.env.REPLO_CF_CONTAINERS_APP_ID;
  if (!accountId || !appId) {
    return null;
  }
  return `https://dash.cloudflare.com/${accountId}/workers/containers/applications/${appId}/instances/${sandboxId}/metrics`;
}

function getModalSandboxUrl({
  sandboxId,
  modalAppId,
}: {
  sandboxId: string;
  modalAppId?: string;
}): string {
  const ONE_DAY_MS = 24 * 60 * 60 * 1000;
  const now = Date.now();
  const end = now / 1000;
  const start = (now - ONE_DAY_MS) / 1000;

  const isDevelopment = process.env.NODE_ENV !== "production";
  let appPath = "/apps/replo/main/deployed/andytown-agent-modal";
  if (isDevelopment) {
    appPath = modalAppId
      ? `/apps/replo/main/${modalAppId}`
      : "/apps/replo/main";
  }
  const url = new URL(`https://modal.com${appPath}`);
  url.searchParams.set("start", String(start));
  url.searchParams.set("end", String(end));
  url.searchParams.set("live", "true");
  url.searchParams.set("activeTab", "sandboxes");
  url.searchParams.set("includeLogContext", "false");
  url.searchParams.set("useInputsTable", "true");
  url.searchParams.set("sandboxId", sandboxId);
  return url.toString();
}

function getSandboxUrl({
  providerSandboxId,
  modalSandboxAppId,
  sandboxType,
}: {
  providerSandboxId?: string;
  modalSandboxAppId?: string;
  sandboxType?: "cloudflare" | "modal";
}): string | undefined {
  if (!providerSandboxId || !sandboxType) {
    return undefined;
  }

  if (sandboxType === "modal") {
    return getModalSandboxUrl({
      sandboxId: providerSandboxId,
      modalAppId: modalSandboxAppId,
    });
  }

  return getCfSandboxUrl(providerSandboxId) ?? undefined;
}

const StateTab: React.FC<{ state: AgentState }> = ({ state }) => {
  const stateWithLinks = React.useMemo(() => {
    const { streamingSessionState: rawStreamingSessionState, ...restState } =
      state;

    const streamingSessionState = rawStreamingSessionState
      ? Object.fromEntries(
          Object.entries(rawStreamingSessionState).map(
            ([sessionUuid, session]) => {
              const sandboxUrl = getSandboxUrl({
                providerSandboxId: session.providerSandboxId,
                modalSandboxAppId: session.modalSandboxAppId,
                sandboxType: session.sandboxType,
              });

              return [
                sessionUuid,
                {
                  previewUrl: session.previewUrl,
                  previewingSha: session.previewingSha,
                  originalSha: session.originalSha,
                  sandboxStatus: session.sandboxStatus,
                  ...(sandboxUrl ? { sandboxUrl } : {}),
                },
              ];
            },
          ),
        )
      : undefined;

    return {
      ...restState,
      ...(state.durableObjectId
        ? { durableObjectUrl: getCfDurableObjectUrl(state.durableObjectId) }
        : {}),
      ...(streamingSessionState ? { streamingSessionState } : {}),
    };
  }, [state]);

  return (
    <div className="flex-1 overflow-auto text-xs">
      <div className="mb-1 text-xs font-semibold text-slate-500">
        State (from state-sync):
      </div>
      {state ? (
        <CollapsibleJsonTree data={stateWithLinks} defaultExpandDepth={1} />
      ) : (
        <span className="text-xs text-slate-400">No state available</span>
      )}
    </div>
  );
};

export { StateTab };
